import type {
  ClassPointRewardTier,
  ClassPointSettings,
  GroupMemberPoints,
  ItemCard,
  MemberItemInstance,
} from '../types/pointItemNote';
import { emptyGroupMemberPoints } from '../types/pointItemNote';
import { memberDisplayPointTotal } from '../services/classMemberOwnedDataService';

/** 그룹 통합 모드에서 발급(클레임) 이력 키 */
export const CLASS_POINT_GRANT_SCOPE_ALL = '__all_groups__';

export type ClassPointGroupScope = {
  groupId: string;
  memberIds: string[];
};

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function grantScopeKey(
  settings: Pick<ClassPointSettings, 'integrateAllGroups'>,
  groupId: string | null | undefined
): string {
  if (settings.integrateAllGroups) return CLASS_POINT_GRANT_SCOPE_ALL;
  return groupId || '';
}

/** 특정 그룹 멤버들의 포인트 합 */
export function computeGroupPointTotal(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  group: GroupMemberPoints,
  memberIds: string[]
): number {
  return memberIds.reduce(
    (sum, mid) => sum + memberDisplayPointTotal(memberPointsByGroup, group, mid),
    0
  );
}

/** @deprecated computeClassPointTotal 사용 */
export function computeClassIntegratedPointTotal(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  group: GroupMemberPoints,
  memberIds: string[]
): number {
  return computeGroupPointTotal(memberPointsByGroup, group, memberIds);
}

/**
 * 학급 포인트 합산.
 * - integrateAllGroups ON: 클래스 모든 그룹 멤버 포인트 합
 * - OFF: 현재 그룹만
 */
export function computeClassPointTotal(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  settings: Pick<ClassPointSettings, 'integrateAllGroups'>,
  opts: {
    currentGroupId: string | null | undefined;
    currentMemberIds: string[];
    allGroups: ClassPointGroupScope[];
  }
): number {
  if (settings.integrateAllGroups) {
    return opts.allGroups.reduce((sum, scope) => {
      const g = memberPointsByGroup[scope.groupId] ?? emptyGroupMemberPoints();
      return sum + computeGroupPointTotal(memberPointsByGroup, g, scope.memberIds);
    }, 0);
  }
  if (!opts.currentGroupId || opts.currentMemberIds.length === 0) return 0;
  const g =
    memberPointsByGroup[opts.currentGroupId] ?? emptyGroupMemberPoints();
  return computeGroupPointTotal(memberPointsByGroup, g, opts.currentMemberIds);
}

function resolveTargetGroups(
  groups: ClassPointGroupScope[],
  settings: ClassPointSettings,
  onlyGroupId?: string
): ClassPointGroupScope[] {
  if (settings.integrateAllGroups) return groups;
  if (onlyGroupId) return groups.filter((g) => g.groupId === onlyGroupId);
  return groups;
}

function isClassPointCardInstance(inst: MemberItemInstance, cardId: string): boolean {
  return (
    inst.category === 'classPoint' &&
    inst.itemCardId === cardId
  );
}

function grantOrUnlockCard(
  instances: MemberItemInstance[],
  memberId: string,
  card: ItemCard,
  now: string
): MemberItemInstance[] {
  const idx = instances.findIndex(
    (i) => i.memberId === memberId && isClassPointCardInstance(i, card.itemCardId)
  );
  if (idx >= 0) {
    const cur = instances[idx];
    if (!cur.isLocked) return instances;
    const next = [...instances];
    next[idx] = { ...cur, isLocked: false };
    return next;
  }
  const inst: MemberItemInstance = {
    id: newId('cpi'),
    memberId,
    itemCardId: card.itemCardId,
    category: 'classPoint',
    remainingUseCount: card.type === 'active' ? card.useCount ?? 1 : undefined,
    isLocked: false,
    acquiredAt: now,
  };
  return [...instances, inst];
}

function lockTierCards(
  instances: MemberItemInstance[],
  memberIds: string[],
  cards: ItemCard[]
): MemberItemInstance[] {
  const cardIds = new Set(cards.map((c) => c.itemCardId));
  const memberSet = new Set(memberIds);
  let changed = false;
  const next = instances.map((inst) => {
    if (!memberSet.has(inst.memberId)) return inst;
    if (!cardIds.has(inst.itemCardId)) return inst;
    // category 누락·레거시 인스턴스도 구간 카드 id면 잠금
    if (inst.category && inst.category !== 'classPoint') return inst;
    if (inst.isLocked) return inst;
    changed = true;
    return { ...inst, category: 'classPoint' as const, isLocked: true };
  });
  return changed ? next : instances;
}

function hasLockedTierCards(
  instances: MemberItemInstance[] | undefined,
  memberIds: string[],
  cards: ItemCard[]
): boolean {
  const cardIds = new Set(cards.map((c) => c.itemCardId));
  const memberSet = new Set(memberIds);
  return (instances ?? []).some(
    (inst) =>
      inst.category === 'classPoint' &&
      inst.isLocked &&
      memberSet.has(inst.memberId) &&
      cardIds.has(inst.itemCardId)
  );
}

function sortedTiers(settings: ClassPointSettings): ClassPointRewardTier[] {
  return [...settings.rewardTiers].sort(
    (a, b) => a.thresholdPoints - b.thresholdPoints || a.id.localeCompare(b.id)
  );
}

export type SyncClassPointRewardsResult = {
  memberPointsByGroup: Record<string, GroupMemberPoints>;
  settings: ClassPointSettings;
  newlyGrantedTierIds: string[];
  touchedGroupIds: string[];
};

/** 게이지 클릭으로 받을 수 있는 구간 (신규 클레임 또는 잠금 해제) */
export function getClaimableClassPointTiers(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  groups: ClassPointGroupScope[],
  settings: ClassPointSettings,
  opts: {
    currentGroupId: string | null | undefined;
    classTotal: number;
  }
): ClassPointRewardTier[] {
  if (!settings.enabled) return [];
  const scopeKey = grantScopeKey(settings, opts.currentGroupId);
  if (!scopeKey && !settings.integrateAllGroups) return [];
  const claimed = new Set(settings.grantedTierByGroup[scopeKey] ?? []);
  const targets = resolveTargetGroups(groups, settings, opts.currentGroupId ?? undefined);

  return sortedTiers(settings).filter((tier) => {
    if (tier.thresholdPoints <= 0 || tier.cards.length === 0) return false;
    if (opts.classTotal < tier.thresholdPoints) return false;
    if (!claimed.has(tier.id)) return true;
    return targets.some((scope) => {
      const g = memberPointsByGroup[scope.groupId];
      return hasLockedTierCards(g?.itemInstances, scope.memberIds, tier.cards);
    });
  });
}

/**
 * 포인트가 구간 아래로 떨어지면 해당 구간 카드 잠금.
 * grantedTier 이력이 비어 있어도(레거시 자동발급 등) 보유 카드 id로 잠근다.
 * 자동 발급/해제는 하지 않음.
 */
export function reconcileClassPointCardLocks(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  groups: ClassPointGroupScope[],
  settings: ClassPointSettings,
  opts: {
    currentGroupId: string | null | undefined;
    classTotal: number;
  }
): SyncClassPointRewardsResult {
  if (!settings.enabled || groups.length === 0) {
    return {
      memberPointsByGroup,
      settings,
      newlyGrantedTierIds: [],
      touchedGroupIds: [],
    };
  }

  const targets = resolveTargetGroups(groups, settings, opts.currentGroupId ?? undefined);
  const tiersBelow = sortedTiers(settings).filter(
    (t) => opts.classTotal < t.thresholdPoints && t.cards.length > 0
  );
  if (tiersBelow.length === 0) {
    return {
      memberPointsByGroup,
      settings,
      newlyGrantedTierIds: [],
      touchedGroupIds: [],
    };
  }

  let nextByGroup = memberPointsByGroup;
  const touchedGroupIds: string[] = [];
  for (const scope of targets) {
    if (scope.memberIds.length === 0) continue;
    const g = nextByGroup[scope.groupId] ?? emptyGroupMemberPoints();
    let itemInstances = [...(g.itemInstances ?? [])];
    let changed = false;
    for (const tier of tiersBelow) {
      const before = itemInstances;
      itemInstances = lockTierCards(itemInstances, scope.memberIds, tier.cards);
      if (itemInstances !== before) changed = true;
    }
    if (!changed) continue;
    nextByGroup = {
      ...nextByGroup,
      [scope.groupId]: { ...g, itemInstances },
    };
    touchedGroupIds.push(scope.groupId);
  }

  return {
    memberPointsByGroup: nextByGroup,
    settings,
    newlyGrantedTierIds: [],
    touchedGroupIds,
  };
}

/**
 * 게이지 클릭 클레임: 미수령 구간 발급 또는 잠금 카드 해제.
 */
export function claimClassPointRewards(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  groups: ClassPointGroupScope[],
  settings: ClassPointSettings,
  opts: {
    currentGroupId: string | null | undefined;
    classTotal: number;
    tierIds?: string[];
  }
): SyncClassPointRewardsResult {
  if (!settings.enabled || groups.length === 0) {
    return {
      memberPointsByGroup,
      settings,
      newlyGrantedTierIds: [],
      touchedGroupIds: [],
    };
  }

  const claimable = getClaimableClassPointTiers(
    memberPointsByGroup,
    groups,
    settings,
    opts
  ).filter((t) => !opts.tierIds || opts.tierIds.includes(t.id));
  if (claimable.length === 0) {
    return {
      memberPointsByGroup,
      settings,
      newlyGrantedTierIds: [],
      touchedGroupIds: [],
    };
  }

  const scopeKey = grantScopeKey(settings, opts.currentGroupId);
  const already = new Set(settings.grantedTierByGroup[scopeKey] ?? []);
  const targets = resolveTargetGroups(groups, settings, opts.currentGroupId ?? undefined);
  const now = new Date().toISOString();
  let nextByGroup = memberPointsByGroup;
  const touchedGroupIds: string[] = [];

  for (const scope of targets) {
    if (scope.memberIds.length === 0) continue;
    let g = nextByGroup[scope.groupId] ?? emptyGroupMemberPoints();
    let itemInstances = [...(g.itemInstances ?? [])];
    for (const tier of claimable) {
      for (const mid of scope.memberIds) {
        for (const card of tier.cards) {
          itemInstances = grantOrUnlockCard(itemInstances, mid, card, now);
        }
      }
      already.add(tier.id);
    }
    nextByGroup = {
      ...nextByGroup,
      [scope.groupId]: { ...g, itemInstances },
    };
    touchedGroupIds.push(scope.groupId);
  }

  return {
    memberPointsByGroup: nextByGroup,
    settings: {
      ...settings,
      grantedTierByGroup: {
        ...settings.grantedTierByGroup,
        [scopeKey]: Array.from(already),
      },
    },
    newlyGrantedTierIds: claimable.map((t) => t.id),
    touchedGroupIds,
  };
}

/** 하위 호환: 저장 시 자동 발급 대신 잠금만 맞춤 */
export function syncClassPointRewards(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  groups: ClassPointGroupScope[],
  settings: ClassPointSettings,
  opts?: { onlyGroupId?: string }
): SyncClassPointRewardsResult {
  const currentGroupId = opts?.onlyGroupId ?? groups[0]?.groupId;
  const classTotal = computeClassPointTotal(memberPointsByGroup, settings, {
    currentGroupId,
    currentMemberIds:
      groups.find((g) => g.groupId === currentGroupId)?.memberIds ?? [],
    allGroups: groups,
  });
  return reconcileClassPointCardLocks(memberPointsByGroup, groups, settings, {
    currentGroupId,
    classTotal,
  });
}

export function syncClassPointRewardsForGroup(
  group: GroupMemberPoints,
  memberIds: string[],
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  groupId: string,
  settings: ClassPointSettings
): { group: GroupMemberPoints; settings: ClassPointSettings; newlyGrantedTierIds: string[] } {
  const merged = {
    ...memberPointsByGroup,
    [groupId]: group,
  };
  const result = syncClassPointRewards(
    merged,
    [{ groupId, memberIds }],
    settings,
    { onlyGroupId: groupId }
  );
  return {
    group: result.memberPointsByGroup[groupId] ?? group,
    settings: result.settings,
    newlyGrantedTierIds: result.newlyGrantedTierIds,
  };
}
